import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft,
  BriefcaseBusiness,
  ChevronLeft,
  Factory,
  Globe2,
  Handshake,
  Search,
  UsersRound,
} from "lucide-react";
import {
  getProfessionalMapSection,
  PROFESSIONAL_MAP_SECTIONS,
} from "@/data/professionalMapData";
import { PROFESSIONAL_MAP_ARTWORK } from "@/data/professionalMapVisualAssets";
import ProfessionalMapHeroLinks from "@/components/professional-map/ProfessionalMapHeroLinks";

const ICONS = {
  users: UsersRound,
  factory: Factory,
  briefcase: BriefcaseBusiness,
  handshake: Handshake,
  search: Search,
};

function SampleVisual({ section, index, item }) {
  const categoryArtwork = section.slug === "masters"
    ? PROFESSIONAL_MAP_ARTWORK.mastersCategories[item.title]
    : section.slug === "suppliers"
      ? PROFESSIONAL_MAP_ARTWORK.suppliersCategories[item.title]
      : section.slug === "vacancies"
        ? PROFESSIONAL_MAP_ARTWORK.workCategories[item.title]
        : section.slug === "services"
          ? PROFESSIONAL_MAP_ARTWORK.servicesCategories[item.title]
          : null;
  if (categoryArtwork && section.slug === "services") return (
    <div className="aspect-[4/5] w-full overflow-hidden bg-[#FAF4EB]">
      {/* Crop only the photography artwork's baked edge into the shared UI frame. */}
      <img src={categoryArtwork} alt=""
        className={`h-full w-full object-cover ${item.title === "Фотосъёмка" ? "scale-[1.014]" : ""}`}
        data-testid={`professional-map-services-artwork-${index}`} />
    </div>
  );
  // The approved partnership artwork is edge-to-edge; its siblings contain
  // an inset ivory frame. Match that framing without changing the asset.
  if (categoryArtwork && section.slug === "vacancies" && item.title === "Партнёрство и проекты") return (
    <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F6EEE6]">
      <div className="absolute inset-x-[1.8%] inset-y-[1.43%] overflow-hidden rounded-[3%] border border-[#FFF8ED]">
        <img src={categoryArtwork} alt=""
          className="h-full w-full scale-[1.04] object-cover"
          data-testid={`professional-map-${section.slug}-artwork-${index}`} />
      </div>
    </div>
  );
  // Supplier Ingredients and Packaging are full-bleed; new Owner-approved
  // WebP artworks already contain an ivory frame. Align via presentation only.
  const supplierNeedsFrame = section.slug === "suppliers" &&
    ["Ингредиенты", "Упаковка"].includes(item.title);
  if (categoryArtwork) return (
    <div className={`aspect-[4/5] w-full overflow-hidden ${supplierNeedsFrame ? "bg-[#FFFCF8] p-[9px] sm:p-[10px]" : "bg-[#FAF4EB]"}`}>
      <img src={categoryArtwork} alt=""
        className={`h-full w-full object-contain ${supplierNeedsFrame ? "rounded-[15px]" : ""}`}
        data-testid={`professional-map-${section.slug}-artwork-${index}`} />
    </div>
  );
  const Icon = ICONS[section.icon] || Globe2;
  const [accent, soft] = section.palette;

  return (
    <div
      className="relative h-[170px] overflow-hidden border-b border-[#6E5544]/10"
      style={{
        background: `linear-gradient(${132 + index * 7}deg, ${soft} 0%, #FBF7EF 58%, ${accent} 150%)`,
      }}
      aria-hidden="true"
    >
      <span
        className="absolute -right-8 -top-8 h-28 w-28 rounded-full border"
        style={{ borderColor: `${accent}80` }}
      />
      <span
        className="absolute left-5 bottom-5 inline-flex h-12 w-12 items-center justify-center rounded-full border bg-white/62 backdrop-blur"
        style={{ borderColor: `${accent}90` }}
      >
        <Icon className="h-5 w-5 text-[#5B493D]" strokeWidth={1.25} />
      </span>
    </div>
  );
}

export default function ProfessionalMapSection() {
  const { sectionSlug } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const section = getProfessionalMapSection(sectionSlug);
  const categoryParam = searchParams.get("category");
  const selectedCategory = (["masters", "suppliers", "vacancies"].includes(section?.slug) || section?.slug === "services") &&
    section.samples.some((item) => item.title === categoryParam)
    ? categoryParam : null;
  const [query, setQuery] = useState("");
  const normalized = query.trim().toLowerCase();

  function submitSectionSearch(event) {
    event.preventDefault();
    if (!section) return;
    // One public search across the four Map sections, scoped to the current one.
    // Keep existing category suggestions while typing; only submit navigates.
    const params = new URLSearchParams({ view: "categories", section: section.slug });
    if (query.trim()) params.set("q", query.trim());
    nav("/professional-map/discover?" + params.toString());
  }

  // Category pages must query real publishable members, never show a hardcoded empty state.
  const [masterResults, setMasterResults] = useState({status: "idle", rows: [], total: 0});
  useEffect(() => {
    if (section?.slug !== "masters" || !selectedCategory) {
      setMasterResults({status: "idle", rows: [], total: 0});
      return;
    }
    let active = true;
    const params = new URLSearchParams({section: "masters", limit: "30"});
    const definition = section.samples.find((item) => item.title === selectedCategory);
    (definition?.roles || []).forEach((role) => params.append("specializations", role));
    setMasterResults({status: "loading", rows: [], total: 0});
    api.get("/professional-map/search?" + params.toString())
      .then(({data}) => {
        if (active) setMasterResults({
          status: "success",
          rows: Array.isArray(data?.results) ? data.results : [],
          total: Number(data?.total || 0),
        });
      })
      .catch(() => {
        if (active) setMasterResults({status: "unavailable", rows: [], total: 0});
      });
    return () => { active = false; };
  }, [section?.slug, selectedCategory]);


  const visibleSamples = useMemo(() => {
    if (!section) return [];
    if (!normalized) return section.samples;
    return section.samples.filter((item) =>
      `${item.title} ${item.subtitle}`.toLowerCase().includes(normalized)
    );
  }, [section, normalized]);

  // Historic fifth section is no longer a directory. Keep old shared URLs usable.
  if (sectionSlug === "requests") return <Navigate to="/professional-map" replace />;

  if (!section) {
    return (
      <div className="min-h-screen bg-[#F7F0E4] flex items-center justify-center px-5 text-[#3A2B22]">
        <div className="max-w-md w-full rounded-3xl border border-[#765944]/20 bg-white/60 p-8 text-center">
          <h1 className="font-serif text-3xl">Раздел не найден</h1>
          <button
            type="button"
            onClick={() => nav("/professional-map")}
            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[#765944]/30 px-4 py-2.5 font-serif text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Вернуться к карте
          </button>
        </div>
      </div>
    );
  }

  const Icon = ICONS[section.icon] || Globe2;
  const [accent, soft] = section.palette;

  return (
    <div
      className="min-h-screen text-[#3A2B22]"
      style={{ background: "linear-gradient(180deg, #F8F1E5 0%, #F2E6D4 100%)" }}
      data-testid={`professional-map-section-${section.slug}`}
    >
      <header className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-4 sm:pt-5" data-testid="professional-map-section-top-navigation">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5">
          <nav className="flex min-h-[44px] flex-wrap items-center gap-x-2 gap-y-1 text-sm" aria-label="Навигация Профессиональной карты">
            <button type="button" onClick={() => nav("/hall")}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-2 -ml-2 hover:bg-white/45" aria-label="В Anokina Studio">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-serif">Anokina Studio</span>
            </button>
            <span className="text-[#A08C7C]" aria-hidden="true">/</span>
            <button type="button" onClick={() => nav("/professional-map")}
              className="min-h-[44px] font-serif text-[#6B5544] hover:text-[#3A2B22]">Профессиональная карта</button>
          </nav>
          <form onSubmit={submitSectionSearch}
            className="relative flex h-11 w-full max-w-[480px] items-center rounded-xl border border-[#765944]/24 bg-white/72 shadow-[0_2px_14px_rgba(74,59,50,0.04)]"
            role="search" aria-label={"Поиск по разделу: " + section.title} data-testid="professional-map-section-header-search">
            <Search className="ml-3 h-4 w-4 shrink-0 text-[#8A7565]" strokeWidth={1.5} aria-hidden="true" />
            <label className="sr-only" htmlFor="pm-section-category-search">Имя, профессия, услуга или товар в разделе {section.title}</label>
            <input id="pm-section-category-search" value={query} onChange={(event) => setQuery(event.target.value)}
              type="search" placeholder={"Категории: " + section.shortTitle + "…"}
              maxLength={180} autoComplete="off"
              className="h-11 min-w-0 flex-1 bg-transparent px-3 text-sm text-[#49372C] outline-none placeholder:text-[#9A887B]"
              data-testid="professional-map-section-search" />
            <button type="submit"
              className="mr-1 min-h-[36px] shrink-0 rounded-lg border border-[#765944]/30 bg-[#FBF7EF]/95 px-3 text-xs font-medium text-[#594435] transition-colors hover:border-[#765944]/50 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]/60"
              aria-label={"Найти в разделе " + section.title}
              data-testid="professional-map-section-find">Найти</button>
          </form>
        </div>
      </header>

      <main className="max-w-[1240px] mx-auto px-5 sm:px-8 pb-16 sm:pb-20">

        {section.slug === "masters" ? (
          <section className="relative mt-4 overflow-hidden rounded-[26px] border border-[#765944]/22 bg-[#F6EEE4] sm:mt-6"
            data-testid="professional-map-masters-hero">
            {/* Independent mobile crop of the original approved image; desktop stays unchanged. */}
            <img src={PROFESSIONAL_MAP_ARTWORK.mastersHeroDesktop} alt=""
              className="block h-[245px] w-full object-cover object-[82%_center] sm:h-[315px] lg:hidden"
              data-testid="professional-map-masters-hero-mobile-artwork" />
            <img src={PROFESSIONAL_MAP_ARTWORK.mastersHeroDesktop} alt=""
              className="hidden w-full lg:absolute lg:inset-0 lg:block lg:h-full lg:object-cover"
              data-testid="professional-map-masters-hero-artwork" />
            <div className="relative z-10 flex flex-col justify-center px-5 py-6 sm:px-9 sm:py-8 lg:min-h-[365px] lg:w-[66%] lg:px-11 lg:py-12">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#886F5C]">
                {section.eyebrow}
              </div>
              <h1 className="mt-2 font-serif text-[clamp(29px,7.8vw,34px)] leading-[1.07] sm:text-[44px] sm:leading-[1.02] lg:text-[48px]">
                {section.title}
              </h1>
              <p className="mt-3 max-w-[28rem] text-sm leading-6 text-[#705C4D] sm:mt-4 sm:text-[15px] lg:max-w-[29rem] xl:max-w-[32rem]">
                Кондитеры, декораторы, технологи, преподаватели,{" "}
                <span className="sm:block">наставники и профильные специалисты.</span>
              </p>
              <ProfessionalMapHeroLinks section={section.slug} />
            </div>
          </section>
        ) : section.slug === "suppliers" ? (
          <section
            className="relative mt-4 overflow-hidden rounded-[26px] border border-[#765944]/22 bg-[#F6EEE4] sm:mt-6"
            data-testid="professional-map-suppliers-hero"
          >
            <img
              src={PROFESSIONAL_MAP_ARTWORK.suppliersHeroMobile}
              alt=""
              className="block h-auto w-full lg:hidden"
              data-testid="professional-map-suppliers-hero-mobile"
            />
            <img
              src={PROFESSIONAL_MAP_ARTWORK.suppliersHeroDesktop}
              alt=""
              className="absolute inset-0 hidden h-full w-full object-cover lg:block"
              data-testid="professional-map-suppliers-hero-desktop"
            />
            <div className="relative z-10 flex flex-col justify-center px-6 py-8 sm:px-9 lg:min-h-[365px] lg:w-[40%] lg:px-8 xl:px-10 lg:py-12">
              <h1 className="font-serif text-[29px] leading-[1.06] sm:text-[40px] lg:text-[35px] xl:text-[40px]">
                <span className="block">Поставщики</span>
                <span className="block whitespace-nowrap">и производство</span>
              </h1>
              <p className="mt-4 max-w-[30rem] text-sm leading-6 text-[#705C4D] sm:text-[15px]">
                {section.description}
              </p>
              <ProfessionalMapHeroLinks section={section.slug} />
            </div>
          </section>
        ) : section.slug === "vacancies" ? (
          <section
            className="relative mt-4 overflow-hidden rounded-[26px] border border-[#765944]/22 bg-[#F6EEE4] sm:mt-6"
            data-testid="professional-map-work-hero"
          >
            <img
              src={PROFESSIONAL_MAP_ARTWORK.workHeroMobile}
              alt=""
              className="block h-auto w-full lg:hidden"
              data-testid="professional-map-work-hero-mobile"
            />
            <img
              src={PROFESSIONAL_MAP_ARTWORK.workHeroDesktop}
              alt=""
              className="absolute inset-0 hidden h-full w-full object-cover lg:block"
              data-testid="professional-map-work-hero-desktop"
            />
            <div className="relative z-10 flex flex-col justify-center px-6 py-8 sm:px-9 lg:min-h-[365px] lg:w-[43%] lg:px-8 xl:px-10 lg:py-12">
              <h1 className="font-serif text-[clamp(26px,7.6vw,35px)] leading-[1.04] sm:text-[44px] lg:text-[38px] xl:text-[44px]" data-testid="professional-map-work-heading">
                <span className="block whitespace-nowrap">Работа</span>
                <span className="block whitespace-nowrap">и сотрудничество</span>
              </h1>
              <p className="mt-4 max-w-[30rem] text-sm leading-6 text-[#705C4D] sm:text-[15px]">
                {section.description}
              </p>
              <ProfessionalMapHeroLinks section={section.slug} />
            </div>
          </section>
        ) : section.slug === "services" ? (
          <section
            className="relative mt-4 overflow-hidden rounded-[26px] border border-[#765944]/22 bg-[#F6EEE4] sm:mt-6"
            data-testid="professional-map-services-hero"
          >
            <img src={PROFESSIONAL_MAP_ARTWORK.servicesHeroMobile} alt=""
              className="block h-auto w-full lg:hidden"
              data-testid="professional-map-services-hero-mobile" />
            <img src={PROFESSIONAL_MAP_ARTWORK.servicesHeroDesktop} alt=""
              className="hidden h-auto w-full lg:block"
              data-testid="professional-map-services-hero-desktop" />
            <div className="relative z-10 flex flex-col justify-center px-6 py-8 sm:px-9 lg:absolute lg:inset-y-0 lg:left-0 lg:w-[40%] lg:px-8 xl:px-10 lg:py-8"
              data-testid="professional-map-services-hero-copy">
              <h1 className="font-serif text-[35px] leading-[1.04] sm:text-[44px] lg:text-[46px]">
                {section.title}
              </h1>
              <p className="mt-4 max-w-[30rem] text-sm leading-6 text-[#705C4D] sm:text-[15px]">
                {section.description}
              </p>
              <ProfessionalMapHeroLinks section={section.slug} />
            </div>
          </section>
        ) : (
          <section
            className="relative overflow-hidden mt-4 sm:mt-6 rounded-[26px] border border-[#765944]/22 bg-white/48"
            style={{
              background: `linear-gradient(128deg, rgba(255,255,255,.72) 0%, ${soft} 68%, ${accent} 155%)`,
            }}
          >
            <div className="grid lg:grid-cols-[1fr_0.48fr] min-h-[260px]">
              <div className="px-6 sm:px-9 lg:px-11 py-8 sm:py-10 flex flex-col justify-center">
                <div className="text-[9px] uppercase tracking-[0.22em] text-[#886F5C]">
                  {section.eyebrow}
                </div>
                <h1 className="font-serif text-[35px] sm:text-[44px] leading-[1.02] mt-2">
                  {section.title}
                </h1>
                <p className="mt-4 max-w-2xl text-sm sm:text-[15px] leading-6 text-[#705C4D]">
                  {section.description}
                </p>

              </div>
              <div className="relative min-h-[190px] lg:min-h-full border-t lg:border-t-0 lg:border-l border-[#765944]/12 overflow-hidden">
                <div className="absolute inset-0 opacity-55"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(111,85,66,.08) 1px, transparent 1px), linear-gradient(90deg, rgba(111,85,66,.08) 1px, transparent 1px)",
                    backgroundSize: "34px 34px",
                  }}
                />
                <span
                  className="absolute left-1/2 top-1/2 inline-flex h-24 w-24 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border bg-white/36"
                  style={{ borderColor: `${accent}95` }}
                  aria-hidden="true"
                >
                  <Icon className="h-10 w-10 text-[#654E3E]" strokeWidth={1.05} />
                </span>
              </div>
            </div>
          </section>
  
        )}



        <section className="pt-7 sm:pt-9">
          <div className="mb-4">
            <div className="text-[9px] uppercase tracking-[0.21em] text-[#8A7463]">
              Категории и направления
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl mt-1">
              {selectedCategory || (section.slug === "masters" ? "Специализации" : "Категории")}
            </h2>
          </div>

          {selectedCategory ? (
            <div className="rounded-2xl border border-[#765944]/18 bg-white/45 px-5 py-6 sm:px-7"
              data-testid="professional-map-category-results">
              {section.slug === "masters" ? (
                masterResults.status === "loading" ? <p role="status" className="text-sm">Ищем опубликованных специалистов…</p>
                : masterResults.status === "unavailable" ? <p role="status" className="text-sm">Поиск временно недоступен.</p>
                : masterResults.rows.length === 0 ? <p role="status" className="text-sm text-[#715D4E]">В этой категории пока нет опубликованных профессиональных страниц.</p>
                : <div className="grid gap-3 sm:grid-cols-2" data-testid="professional-map-real-master-results">
                    {masterResults.rows.filter((person) => person?.id && person?.public_name).map((person) => (
                      <button key={person.id} type="button"
                        onClick={() => nav("/professional-map/profiles/" + encodeURIComponent(person.id))}
                        className="min-h-[80px] rounded-xl border border-[#765944]/20 bg-[#FFFCF8] p-4 text-left hover:bg-[#F6EEE4] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#765944]">
                        <span className="block font-serif text-xl">{person.public_name}</span>
                        <span className="mt-1 block text-xs text-[#715D4E]">{[person.city, person.country].filter(Boolean).join(", ")}</span>
                        {person.specializations?.length > 0 && <span className="mt-2 block text-xs text-[#715D4E]">{person.specializations.join(" · ")}</span>}
                      </button>
                    ))}
                  </div>
              ) : <p role="status" className="text-sm text-[#715D4E]">{section.slug === "vacancies" ? "Пока нет опубликованных предложений по этому направлению." : "В этой категории пока нет опубликованных профессиональных страниц."}</p>}
              <button type="button" onClick={() => nav(`/professional-map/${section.slug}`)}
                className="mt-5 inline-flex min-h-[44px] items-center rounded-xl border border-[#765944]/30 px-4 py-2 font-serif text-sm hover:bg-white/70">
                Показать все категории
              </button>
            </div>
          ) : visibleSamples.length > 0 ? (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${section.slug === "vacancies" ? "lg:grid-cols-3" : section.slug === "suppliers" ? "lg:grid-cols-4" : "lg:grid-cols-5"} gap-3 sm:gap-4`}>
              {visibleSamples.map((item, index) => section.slug === "masters" ? (
                <button
                  key={item.title}
                  type="button"
                  aria-label={`Открыть категорию «${item.title}»`}
                  onClick={() => nav(`/professional-map/masters?category=${encodeURIComponent(item.title)}`)}
                  className="group w-full overflow-hidden rounded-[20px] border border-[#765944]/22 bg-[#FBF7EF]/76 text-left shadow-[0_4px_18px_rgba(74,59,50,0.045)] transition-shadow hover:shadow-[0_9px_24px_rgba(74,59,50,0.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#765944]"
                  data-testid={`professional-map-master-category-${index}`}
                >
                  <SampleVisual section={section} index={index} item={item} />
                  <span className="sr-only">{item.title}</span>
                </button>
              ) : section.slug === "suppliers" ? (
                <button
                  key={item.title}
                  type="button"
                  aria-label={`Открыть категорию «${item.title}»`}
                  onClick={() => nav(`/professional-map/suppliers?category=${encodeURIComponent(item.title)}`)}
                  className="group w-full overflow-hidden rounded-[20px] border border-[#765944]/22 bg-[#FBF7EF]/76 text-left shadow-[0_4px_18px_rgba(74,59,50,0.045)] transition-shadow hover:shadow-[0_9px_24px_rgba(74,59,50,0.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#765944]"
                  data-testid={`professional-map-supplier-category-${index}`}
                >
                  <SampleVisual section={section} index={index} item={item} />
                  <span className="sr-only">{item.title}</span>
                </button>
              ) : section.slug === "vacancies" ? (
                <button
                  key={item.title}
                  type="button"
                  aria-label={`Открыть категорию «${item.title}»`}
                  onClick={() => nav(`/professional-map/vacancies?category=${encodeURIComponent(item.title)}`)}
                  className="group w-full overflow-hidden rounded-[20px] border border-[#E6D9CB] bg-[#FBF7EF]/76 text-left shadow-[0_4px_18px_rgba(74,59,50,0.045)] transition-shadow hover:shadow-[0_9px_24px_rgba(74,59,50,0.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#765944]"
                  data-testid={`professional-map-work-category-${index}`}
                >
                  <SampleVisual section={section} index={index} item={item} />
                  <span className="sr-only">{item.title}</span>
                </button>
              ) : section.slug === "services" ? (
                <button
                  key={item.title}
                  type="button"
                  aria-label={`Открыть категорию «${item.title}»`}
                  onClick={() => nav(`/professional-map/services?category=${encodeURIComponent(item.title)}`)}
                  className="group w-full overflow-hidden rounded-[20px] border border-[#FFF8ED] bg-[#FAF4EB] text-left shadow-[0_4px_18px_rgba(74,59,50,0.045)] transition-shadow hover:shadow-[0_9px_24px_rgba(74,59,50,0.1)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#765944]"
                  data-testid={`professional-map-service-category-${index}`}
                >
                  <SampleVisual section={section} index={index} item={item} />
                  <span className="sr-only">{item.title}</span>
                </button>
              ) : (
                <article
                  key={item.title}
                  className="overflow-hidden rounded-[20px] border border-[#765944]/22 bg-[#FBF7EF]/76 shadow-[0_4px_18px_rgba(74,59,50,0.045)]"
                >
                  <SampleVisual section={section} index={index} item={item} />
                  <div className="px-4 py-4">
                    <h3 className="font-serif text-[17px] leading-tight">{item.title}</h3>
                    <p className="mt-2 text-[11px] leading-[1.5] text-[#796657] min-h-[34px]">
                      {item.subtitle}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#765944]/18 bg-white/45 px-5 py-8 text-center text-sm text-[#7B6859]">
              По запросу ничего не найдено.
            </div>
          )}
        </section>

        <nav className="mt-8 flex flex-wrap gap-2" aria-label="Другие направления карты">
          {PROFESSIONAL_MAP_SECTIONS.filter((item) => item.slug !== section.slug).map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => nav(`/professional-map/${item.slug}`)}
              className="rounded-full border border-[#765944]/22 bg-white/38 px-3 py-2 text-xs text-[#6E5949] hover:bg-white/65 transition-colors"
            >
              {item.shortTitle}
            </button>
          ))}
        </nav>
      </main>
    </div>
  );
}

